import { useQueryClient } from "@tanstack/solid-query";
import localforage from "localforage";
import type { ParentComponent } from "solid-js";
import { createContext, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import { COMPANY_KEY } from "@/api/query-keys";
import type { CompanyMemberRole, CompanyV2 } from "../api/types";

type InitialState = {
  selectedCompany: CompanyV2 | null;
  currentMemberRole: CompanyMemberRole | null;
};

const createCompanyStore = (initialState: InitialState) => {
  const DEFAULT_STATE: InitialState = {
    selectedCompany: initialState.selectedCompany,
    currentMemberRole: initialState.currentMemberRole,
  };

  return createStore<InitialState>(DEFAULT_STATE);
};

const CompanyContext = createContext<ReturnType<typeof createCompanyStore>>();

export const CompanyProvider: ParentComponent<{
  initialState: InitialState;
}> = (props) => {
  const store = createCompanyStore({
    selectedCompany: props.initialState.selectedCompany,
    currentMemberRole: props.initialState.currentMemberRole,
  });

  return (
    <CompanyContext.Provider value={store}>
      {props.children}
    </CompanyContext.Provider>
  );
};

export const useCompanyStore = () => {
  const store = useContext(CompanyContext);
  if (!store) {
    throw new Error("CompanyContext not found");
  }
  return store;
};

export const useCompanyActions = () => {
  const [, setCompanyStore] = useCompanyStore();
  const queryClient = useQueryClient();

  const selectCompany = (company: CompanyV2) => {
    setCompanyStore("selectedCompany", company);
    setCompanyStore("currentMemberRole", null);
    queryClient.resetQueries({ queryKey: [COMPANY_KEY] });
  };

  const setCurrentMemberRole = (role: CompanyMemberRole | null) => {
    setCompanyStore("currentMemberRole", role);
  };

  const clearCompany = async () => {
    setCompanyStore({ selectedCompany: null, currentMemberRole: null });
    await localforage.removeItem("company-store");
  };

  return {
    selectCompany,
    setCurrentMemberRole,
    clearCompany,
  };
};

export const clearCompanyStore = async () => {
  await localforage.removeItem("company-store");
};

export const getSelectedCompanyFromStorage = async () => {
  return null;
};
